import { useEffect, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'

import { DUR_HOVER, DUR_PRESS, prefersReducedMotion } from '../../lib/motion'

// ============================================================================
// THE DAY PAGE'S SMALL PARTS — every one drawn from docs/design/handoff/day-page/*.dc.html.
// Class names beginning `day-` live in index.css's Day block (tokens and motion); everything else
// is a Tailwind utility on a token.
// ============================================================================

/** The keycap: '1', 'D', 'Ctrl+K'. */
export function Kc({ children, large, className }: { children: ReactNode; large?: boolean; className?: string }) {
  return <span className={clsx('day-kc', large && 'day-kc-lg', className)}>{children}</span>
}

/**
 * A TOOLTIP: name, key, and what happens to the item (README → "Tooltips on every control").
 * Rendered inside its control, shown by CSS after a 400 ms hover rest or on keyboard focus.
 */
export function Tip({ name, k, children, align = 'center', up }: { name: string; k?: string; children: ReactNode; align?: 'center' | 'right'; up?: boolean }) {
  return (
    <span className={clsx('day-tip', align === 'right' && 'day-tip-r', up && 'day-tip-up')} role="tooltip">
      <span className="flex items-center gap-2">
        <b className="font-semibold">{name}</b>
        {k && <span className="day-tip-key">{k}</span>}
      </span>
      <span>{children}</span>
    </span>
  )
}

/** The board's `.card`: surface, 1px line, radius 14, the card shadow. */
export function DayCard({ children, className, ...rest }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLElement>) {
  return (
    <section className={clsx('rounded-card border border-line bg-surface shadow-card', className)} {...rest}>
      {children}
    </section>
  )
}

/** The section header: glyph, 13.5px title, the count, and a quiet right-hand note. */
export function SecHead({
  icon,
  title,
  id,
  count,
  note,
  children,
}: {
  icon?: ReactNode
  title: string
  id?: string
  count?: ReactNode
  note?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="flex items-center gap-[9px] px-4 pb-3 pt-[14px]">
      {icon}
      <h2 id={id} className="m-0 text-[13.5px] font-semibold leading-[1.1] text-ink">
        {title}
      </h2>
      {count !== undefined && <span className="text-[11.5px] font-medium text-muted">{count}</span>}
      {children}
      {note && <span className="ml-auto text-[11px] text-microcopy">{note}</span>}
    </div>
  )
}

/** A source line: spans separated by the board's 3px dots. */
export function SrcLine({ parts, className }: { parts: ReactNode[]; className?: string }) {
  const shown = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== '')
  return (
    <div className={clsx('flex flex-wrap items-center gap-x-[6px] gap-y-1 text-[11px] leading-[1.4] text-microcopy', className)}>
      {shown.map((p, i) => (
        <span key={i} className="contents">
          {i > 0 && <span className="inline-block size-[3px] rounded-full bg-faint" aria-hidden />}
          {typeof p === 'string' ? <span>{p}</span> : p}
        </span>
      ))}
    </div>
  )
}

export function Dot({ className }: { className?: string }) {
  return <span className={clsx('day-dot inline-block size-[6px] flex-none rounded-full', className)} aria-hidden />
}

/** The board's `.pill`: 10.5px 600, radius 20, a 1px border in the tone's line. */
export function Pill({ children, tone, className }: { children: ReactNode; tone: 'quiet' | 'orange' | 'warn' | 'plain'; className?: string }) {
  return (
    <span
      className={clsx(
        'whitespace-nowrap rounded-pill border px-2 py-px text-[10.5px] font-semibold leading-[1.5]',
        tone === 'orange' && 'border-orange-line bg-orange-fill text-orange',
        tone === 'warn' && 'border-warning-line bg-warning-fill text-warning',
        tone === 'quiet' && 'border-line bg-fill-2 text-muted',
        tone === 'plain' && 'border-line bg-fill-2 text-ink-3',
        className,
      )}
    >
      {children}
    </span>
  )
}

/** A skeleton bar (Loading board): `fill`, or `line-3` for the second tone. */
export function Sk({ w, h, second, round, className }: { w: string | number; h: number; second?: boolean; round?: number; className?: string }) {
  return (
    <span
      className={clsx('block flex-none', second ? 'bg-line-3' : 'bg-fill', className)}
      style={{ width: typeof w === 'number' ? `${w}px` : w, height: `${h}px`, borderRadius: `${round ?? 6}px` }}
      aria-hidden
    />
  )
}

/**
 * A COUNT THAT ROLLS (14a, 14c, 14d): the old digit lifts out 4px, the new one rises 5px in.
 * Under reduced motion the global rule collapses the transition to the swap alone.
 */
export function RollingCount({ value, className }: { value: number | string; className?: string }) {
  const [shown, setShown] = useState({ old: value, cur: value, ph: 'idle' as 'idle' | 'pre' | 'run' })
  useEffect(() => {
    if (value === shown.cur) return
    setShown({ old: shown.cur, cur: value, ph: 'pre' })
    const raf = requestAnimationFrame(() => setShown((s) => (s.ph === 'pre' ? { ...s, ph: 'run' } : s)))
    // Back to rest once the 180 ms swap is over (0 under reduced motion — the swap is instant).
    const done = setTimeout(() => setShown((s) => ({ ...s, ph: 'idle' })), prefersReducedMotion() ? 0 : DUR_HOVER * 2)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(done)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  // At rest only the value is in the DOM — the outgoing digit exists just for the swap, so copying
  // the text (or a screen reader) never meets "23" where the page shows "3".
  return (
    <span className={clsx('day-roll', className)} data-ph={shown.ph}>
      {shown.ph !== 'idle' && (
        <span className="day-roll-old" aria-hidden>
          {shown.old}
        </span>
      )}
      <span className="day-roll-new">{shown.cur}</span>
    </span>
  )
}

/**
 * MENUS LEAVE FASTER THAN THEY ARRIVE (14h): open is a CSS entrance; close keeps the menu mounted
 * for the 90 ms press fade, then unmounts. Under reduced motion it unmounts at once.
 */
export function usePresence(open: boolean): { mounted: boolean; closing: boolean } {
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    if (open) {
      setMounted(true)
      setClosing(false)
      return
    }
    if (!mounted) return
    if (prefersReducedMotion()) {
      setMounted(false)
      return
    }
    setClosing(true)
    timer.current = setTimeout(() => {
      setMounted(false)
      setClosing(false)
    }, DUR_PRESS)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  return { mounted, closing }
}
